import { Button, Modal, Tabs, type TabsTabProps } from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useMeteringOrMarketLocations } from "../../hooks/useMeteringOrMarketLocations";
import { useMeters } from "../../hooks/useMeters";
import { useMissingObjectData } from "../../hooks/useMissingObjectData";
import { useSiteCategories } from "../../hooks/useSiteCategories";
import { ModalFooter } from "../../mantine/components/ModalFooter/ModalFooter";
import { TabWithHighlightAndError } from "../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import { useToast } from "../../mantine/notifications/useToast";
import type { BaseWizardProps } from "../../types/wizard.types";
import type {
  Meter,
  MeteringOrMarketLocation
} from "../../utils/backend-types";
import { ObjectName } from "../../utils/enums";
import { getLinkToComponentEditModal } from "../../utils/getLinkToComponentEditModal";
import { isPremiumSite } from "../../utils/isPremiumSite";
import { buildFieldNameToLabelMap } from "../../utils/react-hook-form/buildFieldNameToLabelMap";
import { camelize } from "../../utils/SnakeCamelConversion";
import { CounterDataView } from "../CounterDataView/CounterDataView";
import { HistoryTab } from "../HistoryTab/HistoryTab";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../StaffViewToggle/useShouldShowStaffView";
import { useWizardStack } from "../WizardStack/WizardStackContext";
import { useMeter } from "./hooks/useMeter";
import {
  type CounterUpdateRequestData,
  useMeterEnergyDataMutation
} from "./hooks/useMeterEnergyDataMutation";
import { useMeteringConceptMeter } from "./hooks/useMeteringConceptMeter";
import { useMeterMutation } from "./hooks/useMeterMutation";
import { MesskonzeptForm } from "./MesskonzeptForm/MesskonzeptForm";
import { MESSKONZEPT_FORM_FIELD_DATA } from "./MesskonzeptForm/MesskonzeptForm.constants";
import type { MeterResponse } from "./Meter.types";
import { MeterForm } from "./MeterForm/MeterForm";
import { METER_FORM_FIELD_DATA } from "./MeterForm/MeterForm.constants";
import type { MeterFormData } from "./MeterForm/MeterForm.types";
import "./MeterWizard.scss";

enum MeterWizardTab {
  General = "general",
  Messkonzept = "messkonzept",
  Messwerte = "messwerte",
  Historie = "historie"
}

type TabProps = Omit<TabsTabProps, "value"> & {
  value: MeterWizardTab;
  label: string;
};

const TABS: Array<TabProps> = [
  { value: MeterWizardTab.General, label: "Stammdaten" },
  { value: MeterWizardTab.Messkonzept, label: "Messkonzept" },
  { value: MeterWizardTab.Messwerte, label: "Messwerte" },
  { value: MeterWizardTab.Historie, label: "Historie" }
];

type MeterWizardProps = BaseWizardProps & {
  projectId: string;
  siteId: number;
  variantId: number;
  title?: string;
};

function MeterWizard({
  graphCoordinates = { x: 0, y: 0 },
  isOpen,
  projectId,
  siteId,
  title: titleOverride,
  variantId,
  onClose,
  ...modeProps
}: MeterWizardProps) {
  const navigate = useNavigate();
  const { depth } = useWizardStack();
  const { showToast, showErrorToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: locationState } = useLocation();
  const {
    siteCategories,
    isLoading: isLoadingSiteCategories,
    error: siteCategoriesError
  } = useSiteCategories(siteId ?? null);
  const { createMutation, editMutation } = useMeterMutation(siteId);
  const { shouldShowStaffView } = useShouldShowStaffView();
  const {
    data: meteringOrMarketLocations,
    isLoading: isLoadingLocations,
    error: locationsError
  } = useMeteringOrMarketLocations({ siteId });
  const {
    data: meters,
    isLoading: isLoadingMeters,
    error: metersError
  } = useMeters(siteId);

  const meterId = modeProps.mode === "edit" ? modeProps.objectId : undefined;

  const {
    data: meter,
    isLoading: isMeterLoading,
    error: meterError
  } = useMeter(meterId);

  const {
    isLoading: isMissingDataLoading,
    error: missingDataError,
    fieldsThatCouldBeMissing,
    shouldFieldBeHighlighted
  } = useMissingObjectData(meterId, siteId);

  const meterEnergyDataMutation = useMeterEnergyDataMutation(meterId);

  const isPremium = isPremiumSite(siteCategories, siteId);

  const {
    data: meteringConceptMeter,
    isLoading: isMeteringConceptMeterLoading,
    error: meteringConceptMeterError
  } = useMeteringConceptMeter(isPremium ? meterId : undefined);

  const [numErrorsByTab, setNumErrorsByTab] = useState<
    Partial<Record<MeterWizardTab, number>>
  >({});
  const [highlightedTabs, setHighlightedTabs] = useState<Array<MeterWizardTab>>(
    []
  );
  const [counterUpdateRequestData, setCounterUpdateRequestData] =
    useState<CounterUpdateRequestData>({});

  useEffect(() => {
    if (!isOpen) {
      setCounterUpdateRequestData({});
    }
  }, [isOpen]);

  let availableLocations: Array<MeteringOrMarketLocation> | undefined;
  if (meteringOrMarketLocations && meters) {
    availableLocations = getAvailableLocations(
      meteringOrMarketLocations,
      meters,
      meterId
    );
  }

  const currentMeterFromList = meters?.find((m) => m.id === meterId);

  async function handleSubmitCreate(formData: MeterFormData) {
    return createMutation.mutateAsync(
      {
        ...formData,
        site: siteId,
        sketch_element: {
          x_position: graphCoordinates.x,
          y_position: graphCoordinates.y
        }
      },
      {
        onSuccess: (response) => handleCreateSuccess(response.data)
      }
    );
  }

  function handleSubmitUpdate(formData: MeterFormData, id: number) {
    return editMutation.mutateAsync(
      { ...formData, id },
      {
        onSuccess: () => {
          onClose();
          showToast({
            type: "success",
            message: "Die Änderungen wurden gespeichert."
          });
        }
      }
    );
  }

  function handleCreateSuccess(createdMeter: MeterResponse) {
    // On premium sites the meter has additional Messkonzept data to fill in, so we
    // navigate to this same wizard's edit route at the Messkonzept tab right after
    // creation.
    if (isPremium) {
      const path = getLinkToComponentEditModal(
        ObjectName.Meter,
        createdMeter.id,
        projectId,
        siteId
      );
      if (path) {
        onClose();
        navigate(`${path}?tab=messkonzept`);
        return;
      }
    }

    onClose();
    showToast({ type: "success", message: "Der Zähler wurde erstellt." });
  }

  function handleSaveCounterData() {
    meterEnergyDataMutation.mutate(counterUpdateRequestData, {
      onSuccess: () => {
        onClose();
        showToast({
          type: "success",
          message: "Die Änderungen wurden gespeichert."
        });
      },
      onError: (error) => showErrorToast(error)
    });
  }

  const handleChangeGeneralNumErrors = useCallback((numErrors: number) => {
    setNumErrorsByTab((current) =>
      withTabErrorCount(current, MeterWizardTab.General, numErrors)
    );
  }, []);

  const handleChangeMesskonzeptNumErrors = useCallback((numErrors: number) => {
    setNumErrorsByTab((current) =>
      withTabErrorCount(current, MeterWizardTab.Messkonzept, numErrors)
    );
  }, []);

  const handleChangeGeneralNumHighlightedFields = useCallback(
    (numHighlightedFields: number) => {
      setHighlightedTabs((current) =>
        withTabHighlight(
          current,
          MeterWizardTab.General,
          numHighlightedFields > 0
        )
      );
    },
    []
  );

  // The Messkonzept tab highlight is derived at the wizard level rather than
  // reported up from the form, because the tab is not the default-active one:
  // an inactive tab's panel is rendered via React's `<Activity mode="hidden">`,
  // which defers the form's effects, so a form-reported count would not fire
  // until the user opened the tab. Deriving it here shows the highlight on load
  // and refreshes it after a save invalidates the site wizard. The site-wizard
  // missing-field names are camelCase (useSiteWizard camelizes them), so the
  // snake_case Messkonzept field names are camelized before comparison.
  const meteringConceptHasHighlightedFields = useMemo(() => {
    const meteringConceptFieldNames = Object.keys(
      MESSKONZEPT_FORM_FIELD_DATA
    ).map((fieldName) => camelize(fieldName));

    return fieldsThatCouldBeMissing.some((field) =>
      meteringConceptFieldNames.includes(field)
    );
  }, [fieldsThatCouldBeMissing]);

  useEffect(() => {
    setHighlightedTabs((current) =>
      withTabHighlight(
        current,
        MeterWizardTab.Messkonzept,
        meteringConceptHasHighlightedFields
      )
    );
  }, [meteringConceptHasHighlightedFields]);

  const fieldNameToLabelMap = buildFieldNameToLabelMap<MeterFormData>(
    METER_FORM_FIELD_DATA
  );

  function getActiveTab(): MeterWizardTab {
    if (modeProps.mode !== "edit") {
      return MeterWizardTab.General;
    }

    const requestedTab = searchParams.get("tab");
    const validTabValues = TABS.filter(
      (tab) => tab.value !== MeterWizardTab.Messkonzept || isPremium
    ).map((tab) => tab.value);

    return (
      validTabValues.find((value) => value === requestedTab) ??
      MeterWizardTab.General
    );
  }

  const activeTab = getActiveTab();
  const isEditMode = modeProps.mode === "edit";

  const isLoading =
    isLoadingLocations ||
    isLoadingSiteCategories ||
    (isEditMode && (isMeterLoading || isMissingDataLoading || isLoadingMeters));
  const error =
    locationsError ??
    siteCategoriesError ??
    (isEditMode ? meterError || missingDataError || metersError : null);

  const title =
    titleOverride ??
    (meter?.name
      ? `${meter.name} bearbeiten`
      : isEditMode
        ? "Zähler bearbeiten"
        : "Zähler erstellen");

  function getFormModeProps() {
    if (modeProps.mode === "create") {
      return { mode: "create" as const };
    }

    if (meter) {
      return {
        mode: "edit" as const,
        wizardObject: meter,
        fieldsThatCouldBeMissing,
        shouldFieldBeHighlighted
      };
    }

    return null;
  }

  const formModeProps = getFormModeProps();

  return (
    <Modal
      className="MeterWizard"
      closeOnEscape={depth === 0}
      opened={isOpen}
      size="xl"
      title={title}
      onClose={onClose}
    >
      <LoadOrError error={error} loading={isLoading}>
        <Tabs
          value={activeTab}
          onChange={(newTab) => {
            if (!newTab) {
              return;
            }

            searchParams.set("tab", newTab);
            setSearchParams(searchParams, { state: locationState });
          }}
        >
          {isEditMode && (
            <Tabs.List>
              {TABS.filter(
                (tab) => tab.value !== MeterWizardTab.Messkonzept || isPremium
              ).map((tab) => (
                <TabWithHighlightAndError
                  component="a"
                  highlighted={highlightedTabs.includes(tab.value)}
                  key={tab.value}
                  numErrors={numErrorsByTab[tab.value] ?? 0}
                  value={tab.value}
                >
                  {tab.label}
                </TabWithHighlightAndError>
              ))}
            </Tabs.List>
          )}
          <Tabs.Panel value={MeterWizardTab.General}>
            {availableLocations && formModeProps && (
              <MeterForm
                locations={availableLocations}
                showStaffView={shouldShowStaffView}
                onCancel={onClose}
                onChangeNumErrors={handleChangeGeneralNumErrors}
                onChangeNumHighlightedFields={
                  handleChangeGeneralNumHighlightedFields
                }
                onSubmitCreate={handleSubmitCreate}
                onSubmitUpdate={handleSubmitUpdate}
                {...formModeProps}
              />
            )}
          </Tabs.Panel>
          {isEditMode && meterId && isPremium && (
            <Tabs.Panel value={MeterWizardTab.Messkonzept}>
              <LoadOrError
                error={meteringConceptMeterError}
                loading={isMeteringConceptMeterLoading}
              >
                {meteringConceptMeter && (
                  <MesskonzeptForm
                    fieldsThatCouldBeMissing={fieldsThatCouldBeMissing}
                    meterId={meterId}
                    meteringConceptData={meteringConceptMeter}
                    shouldFieldBeHighlighted={shouldFieldBeHighlighted}
                    siteId={siteId}
                    onChangeNumErrors={handleChangeMesskonzeptNumErrors}
                    onClose={onClose}
                  />
                )}
              </LoadOrError>
            </Tabs.Panel>
          )}
          {isEditMode && meterId && currentMeterFromList && (
            <Tabs.Panel value={MeterWizardTab.Messwerte}>
              <CounterDataView
                isGasConnectionDataView={false}
                measurementType={currentMeterFromList.measurementType}
                meterId={meterId}
                variantId={variantId}
                onSetRequestData={setCounterUpdateRequestData}
              />
              <ModalFooter>
                <Button
                  disabled={meterEnergyDataMutation.isPending}
                  variant="default"
                  onClick={onClose}
                >
                  Abbrechen
                </Button>
                <Button
                  color="secondary"
                  loading={meterEnergyDataMutation.isPending}
                  onClick={handleSaveCounterData}
                >
                  Speichern
                </Button>
              </ModalFooter>
            </Tabs.Panel>
          )}
          {isEditMode && meterId && (
            <Tabs.Panel value={MeterWizardTab.Historie}>
              <HistoryTab
                entityId={meterId}
                entityType={ObjectName.Meter}
                fieldNameToLabelMap={fieldNameToLabelMap}
              />
            </Tabs.Panel>
          )}
        </Tabs>
      </LoadOrError>
    </Modal>
  );
}

/** Adds or removes a tab from the highlighted-tabs list, returning the same
 * reference when nothing changes so React bails out of the state update and we
 * skip needless re-renders. */
function withTabHighlight(
  currentTabs: Array<MeterWizardTab>,
  tab: MeterWizardTab,
  isHighlighted: boolean
): Array<MeterWizardTab> {
  const alreadyHighlighted = currentTabs.includes(tab);
  if (isHighlighted === alreadyHighlighted) {
    return currentTabs;
  }

  return isHighlighted
    ? [...currentTabs, tab]
    : currentTabs.filter((value) => value !== tab);
}

/** Records a tab's error count, returning the same reference when unchanged. */
function withTabErrorCount(
  current: Partial<Record<MeterWizardTab, number>>,
  tab: MeterWizardTab,
  count: number
): Partial<Record<MeterWizardTab, number>> {
  if (current[tab] === count) {
    return current;
  }

  return { ...current, [tab]: count };
}

function getAvailableLocations(
  locations: Array<MeteringOrMarketLocation>,
  meters: Array<Meter>,
  excludeMeterId?: number
): Array<MeteringOrMarketLocation> {
  const includedMeters = meters.filter((meter) => meter.id !== excludeMeterId);
  const allLocationIds = includedMeters.flatMap((meter) => [
    meter.locationFeedin,
    meter.locationFeedout
  ]);
  const assignedLocationIds = allLocationIds.filter(
    (id): id is number => id !== null
  );
  const uniqueAssignedLocationIds = new Set(assignedLocationIds);

  return locations.filter(
    (location) => !uniqueAssignedLocationIds.has(location.id)
  );
}

export { MeterWizard, MeterWizardProps };
